import { useState } from "react";
import { useUsuarios } from "@/features/usuarios/hooks/useUsuarios";
import { usePerfisLista } from "@/features/permissoes/hooks/usePermissoesPerfil";
import { useEmpresas } from "@/features/empresa/hooks/useEmpresa";
import type { Usuario, CriarUsuarioRequest, AtualizarUsuarioRequest } from "@/features/usuarios/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Pencil, UserX, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { usePaginacaoLocal } from "@/hooks/usePaginacaoLocal";
import { PaginacaoRodape } from "@/components/PaginacaoRodape";

// ── Form state ────────────────────────────────────────────────────────────────

interface FormState {
  login: string;
  nome: string;
  email: string;
  senha: string;
  isAtivo: boolean;
  isAdminGlobal: boolean;
  perfilIds: number[];
  referenciaPos: string;
  ativoPos: boolean;
  ativoMobile: boolean;
  numeroCelular: string;
  permiteDigitarComanda: boolean;
  senhaMobile: string;
  empresaIds: number[];
}

const EMPTY_FORM: FormState = {
  login: "", nome: "", email: "", senha: "",
  isAtivo: true, isAdminGlobal: false, perfilIds: [],
  referenciaPos: "", ativoPos: false, ativoMobile: false,
  numeroCelular: "", permiteDigitarComanda: false, senhaMobile: "", empresaIds: [],
};

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Usuarios() {
  const { query, criar, atualizar, resetSenha, inativar } = useUsuarios();
  const { perfis: perfisCombo } = usePerfisLista();
  const { data: empresas = [] } = useEmpresas();

  const [busca,        setBusca]        = useState("");
  const [dialogOpen,   setDialogOpen]   = useState(false);
  const [senhaOpen,    setSenhaOpen]    = useState(false);
  const [inativarId,   setInativarId]   = useState<number | null>(null);
  const [editingUser,  setEditingUser]  = useState<Usuario | null>(null);
  const [form,         setForm]         = useState<FormState>(EMPTY_FORM);
  const [novaSenha,    setNovaSenha]    = useState("");
  const [senhaUserId,  setSenhaUserId]  = useState<number | null>(null);

  const usuarios = query.data?.itens ?? [];
  const filtrados = busca
    ? usuarios.filter(u =>
        u.nome.toLowerCase().includes(busca.toLowerCase()) ||
        u.login.toLowerCase().includes(busca.toLowerCase()) ||
        (u.email ?? "").toLowerCase().includes(busca.toLowerCase()))
    : usuarios;

  const paginacao = usePaginacaoLocal(filtrados);

  // ── Dialog handlers ──────────────────────────────────────────────────────

  const openNew = () => {
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (u: Usuario) => {
    setEditingUser(u);
    setForm({
      login:         u.login,
      nome:          u.nome,
      email:         u.email ?? "",
      senha:         "",
      isAtivo:       u.isAtivo,
      isAdminGlobal: u.isAdminGlobal,
      perfilIds:     u.perfis.map(p => p.id),
      referenciaPos: u.referenciaPos ?? "",
      ativoPos:      u.ativoPos,
      ativoMobile:   u.ativoMobile,
      numeroCelular: u.numeroCelular ?? "",
      permiteDigitarComanda: u.permiteDigitarComanda,
      senhaMobile:   "",
      empresaIds:    u.empresasLiberadas.map(e => e.id),
    });
    setDialogOpen(true);
  };

  const openResetSenha = (u: Usuario) => {
    setSenhaUserId(u.id);
    setNovaSenha("");
    setSenhaOpen(true);
  };

  const togglePerfil = (id: number) =>
    setForm(f => ({
      ...f,
      perfilIds: f.perfilIds.includes(id)
        ? f.perfilIds.filter(p => p !== id)
        : [...f.perfilIds, id],
    }));

  const toggleEmpresa = (id: number) =>
    setForm(f => ({
      ...f,
      empresaIds: f.empresaIds.includes(id)
        ? f.empresaIds.filter(e => e !== id)
        : [...f.empresaIds, id],
    }));

  // ── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const posMobile = {
        referenciaPos: form.referenciaPos || undefined,
        ativoPos: form.ativoPos,
        ativoMobile: form.ativoMobile,
        numeroCelular: form.numeroCelular || undefined,
        permiteDigitarComanda: form.permiteDigitarComanda,
        empresaIds: form.empresaIds,
      };
      if (editingUser) {
        const req: AtualizarUsuarioRequest = {
          nome: form.nome, email: form.email || undefined,
          isAtivo: form.isAtivo, isAdminGlobal: form.isAdminGlobal,
          perfilIds: form.perfilIds,
          ...posMobile,
          // só envia senhaMobile se o usuário digitou algo (null = não altera)
          senhaMobile: form.senhaMobile ? form.senhaMobile : undefined,
        };
        await atualizar.mutateAsync({ id: editingUser.id, req });
        toast.success("Usuário atualizado.");
      } else {
        const req: CriarUsuarioRequest = {
          login: form.login, nome: form.nome, email: form.email || undefined,
          senha: form.senha, isAtivo: form.isAtivo, isAdminGlobal: form.isAdminGlobal,
          perfilIds: form.perfilIds,
          ...posMobile,
          senhaMobile: form.senhaMobile || undefined,
        };
        await criar.mutateAsync(req);
        toast.success("Usuário criado.");
      }
      setDialogOpen(false);
    } catch { /* global toast */ }
  };

  const handleResetSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senhaUserId) return;
    try {
      await resetSenha.mutateAsync({ id: senhaUserId, req: { novaSenha } });
      toast.success("Senha redefinida com sucesso.");
      setSenhaOpen(false);
    } catch { /* global toast */ }
  };

  const handleInativar = async () => {
    if (!inativarId) return;
    try {
      await inativar.mutateAsync(inativarId);
      toast.success("Usuário inativado.");
    } catch { /* global toast */ }
    setInativarId(null);
  };

  if (query.isLoading) return <div className="p-6">Carregando...</div>;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Usuários</h1>
          <p className="text-muted-foreground">Gerencie os usuários do sistema</p>
        </div>
        <Button onClick={openNew}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Busca */}
      <div className="max-w-sm">
        <Input
          placeholder="Buscar por nome, login ou e-mail..."
          value={busca}
          onChange={e => setBusca(e.target.value)}
        />
      </div>

      {/* Tabela */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Login</TableHead>
              <TableHead>E-mail</TableHead>
              <TableHead>Perfis</TableHead>
              <TableHead>Último acesso</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="w-[110px]">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginacao.itensPaginados.map(u => (
              <TableRow key={u.id}>
                <TableCell className="font-medium">
                  {u.nome}
                  {u.isAdminGlobal && (
                    <Badge variant="outline" className="ml-2 text-[10px]">Admin</Badge>
                  )}
                </TableCell>
                <TableCell className="font-mono text-sm">{u.login}</TableCell>
                <TableCell>{u.email || "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {u.perfis.length > 0
                      ? u.perfis.map(p => (
                          <Badge key={p.id} variant="secondary" className="text-[11px]">{p.nome}</Badge>
                        ))
                      : <span className="text-muted-foreground text-sm">—</span>
                    }
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {u.dtUltimoLogin
                    ? format(new Date(u.dtUltimoLogin), "dd/MM/yyyy HH:mm", { locale: ptBR })
                    : "—"}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={u.isAtivo ? "success" : "secondary"}>
                    {u.isAtivo ? "Ativo" : "Inativo"}
                  </Badge>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(u)} title="Editar">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => openResetSenha(u)} title="Redefinir senha">
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    {u.isAtivo && (
                      <Button variant="ghost" size="icon" onClick={() => setInativarId(u.id)} title="Inativar">
                        <UserX className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {paginacao.itensPaginados.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                  {busca ? "Nenhum usuário encontrado." : "Nenhum usuário cadastrado."}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <PaginacaoRodape
        pagina={paginacao.pagina}
        totalPaginas={paginacao.totalPaginas}
        totalItens={paginacao.totalItens}
        primeiroItem={paginacao.primeiroItem}
        ultimoItem={paginacao.ultimoItem}
        tamanhoPagina={paginacao.tamanhoPagina}
        onPaginaChange={paginacao.setPagina}
        onTamanhoPaginaChange={paginacao.setTamanhoPagina}
      />

      {/* Dialog criar/editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Editar Usuário" : "Novo Usuário"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="dados" className="space-y-4">
              <TabsList>
                <TabsTrigger value="dados">Dados</TabsTrigger>
                <TabsTrigger value="perfis">Perfis de Acesso</TabsTrigger>
                <TabsTrigger value="posmobile">POS / Mobile</TabsTrigger>
                <TabsTrigger value="empresas">Empresas</TabsTrigger>
              </TabsList>

              {/* Tab: Dados */}
              <TabsContent value="dados" className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="login">Login *</Label>
                    <Input
                      id="login"
                      maxLength={60}
                      value={form.login}
                      onChange={e => setForm(f => ({ ...f, login: e.target.value }))}
                      disabled={!!editingUser}
                      required
                    />
                  </div>
                  {!editingUser && (
                    <div className="space-y-1">
                      <Label htmlFor="senha">Senha *</Label>
                      <Input
                        id="senha"
                        type="password"
                        minLength={6}
                        value={form.senha}
                        onChange={e => setForm(f => ({ ...f, senha: e.target.value }))}
                        required
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-1">
                  <Label htmlFor="nome">Nome completo *</Label>
                  <Input
                    id="nome"
                    maxLength={100}
                    value={form.nome}
                    onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                    required
                  />
                </div>

                <div className="space-y-1">
                  <Label htmlFor="email">E-mail</Label>
                  <Input
                    id="email"
                    type="email"
                    maxLength={100}
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  />
                </div>

                <div className="flex gap-6">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isAtivo"
                      checked={form.isAtivo}
                      onCheckedChange={v => setForm(f => ({ ...f, isAtivo: v }))}
                    />
                    <Label htmlFor="isAtivo">Ativo</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="isAdminGlobal"
                      checked={form.isAdminGlobal}
                      onCheckedChange={v => setForm(f => ({ ...f, isAdminGlobal: v }))}
                    />
                    <Label htmlFor="isAdminGlobal">Administrador global</Label>
                  </div>
                </div>
              </TabsContent>

              {/* Tab: Perfis */}
              <TabsContent value="perfis" className="mt-2">
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {perfisCombo.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">
                      Nenhum perfil cadastrado.
                    </p>
                  )}
                  {perfisCombo.map(p => (
                    <div key={p.id} className="flex items-center gap-3 py-1">
                      <Checkbox
                        id={`perfil-${p.id}`}
                        checked={form.perfilIds.includes(p.id)}
                        onCheckedChange={() => togglePerfil(p.id)}
                      />
                      <Label htmlFor={`perfil-${p.id}`} className="cursor-pointer font-normal">
                        {p.nome}
                      </Label>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Tab: POS / Mobile */}
              <TabsContent value="posmobile" className="space-y-4 mt-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <Label htmlFor="refPos">Referência POS</Label>
                    <Input id="refPos" maxLength={60} value={form.referenciaPos}
                      onChange={e => setForm(f => ({ ...f, referenciaPos: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="numCel">Celular de pedidos</Label>
                    <Input id="numCel" maxLength={25} value={form.numeroCelular}
                      onChange={e => setForm(f => ({ ...f, numeroCelular: e.target.value }))} />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="senhaMobile">Senha Mobile{editingUser ? " (deixe em branco para manter)" : ""}</Label>
                  <Input id="senhaMobile" type="password" minLength={4} value={form.senhaMobile}
                    onChange={e => setForm(f => ({ ...f, senhaMobile: e.target.value }))} />
                  {editingUser?.temSenhaMobile && !form.senhaMobile && (
                    <p className="text-xs text-muted-foreground">Senha mobile já definida.</p>
                  )}
                </div>
                <div className="flex flex-wrap gap-6">
                  <div className="flex items-center gap-2">
                    <Switch id="ativoPos" checked={form.ativoPos}
                      onCheckedChange={v => setForm(f => ({ ...f, ativoPos: v }))} />
                    <Label htmlFor="ativoPos">Ativo POS</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="ativoMobile" checked={form.ativoMobile}
                      onCheckedChange={v => setForm(f => ({ ...f, ativoMobile: v }))} />
                    <Label htmlFor="ativoMobile">Ativo Mobile</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch id="permiteComanda" checked={form.permiteDigitarComanda}
                      onCheckedChange={v => setForm(f => ({ ...f, permiteDigitarComanda: v }))} />
                    <Label htmlFor="permiteComanda">Permite digitar comanda</Label>
                  </div>
                </div>
              </TabsContent>

              {/* Tab: Empresas liberadas */}
              <TabsContent value="empresas" className="mt-2">
                <p className="text-xs text-muted-foreground mb-2">
                  Empresas que este usuário pode acessar. Se nenhuma for marcada, será liberado apenas para a empresa atual.
                </p>
                <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                  {empresas.length === 0 && (
                    <p className="text-sm text-muted-foreground py-4 text-center">Nenhuma empresa cadastrada.</p>
                  )}
                  {empresas.map(e => (
                    <div key={e.id} className="flex items-center gap-3 py-1">
                      <Checkbox
                        id={`empresa-${e.id}`}
                        checked={form.empresaIds.includes(e.id)}
                        onCheckedChange={() => toggleEmpresa(e.id)}
                      />
                      <Label htmlFor={`empresa-${e.id}`} className="cursor-pointer font-normal">
                        {e.nomeFantasia || e.razaoSocial}
                      </Label>
                    </div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={criar.isPending || atualizar.isPending}>
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog reset senha */}
      <Dialog open={senhaOpen} onOpenChange={setSenhaOpen}>
        <DialogContent className="sm:max-w-[380px]">
          <DialogHeader>
            <DialogTitle>Redefinir Senha</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetSenha} className="space-y-4">
            <div className="space-y-1">
              <Label htmlFor="novaSenha">Nova senha *</Label>
              <Input
                id="novaSenha"
                type="password"
                minLength={6}
                value={novaSenha}
                onChange={e => setNovaSenha(e.target.value)}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSenhaOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={resetSenha.isPending}>
                Redefinir
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* AlertDialog inativar */}
      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar usuário</AlertDialogTitle>
            <AlertDialogDescription>
              O usuário perderá acesso ao sistema. Deseja continuar?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleInativar}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
